import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  ClientApiService,
  FileApiService,
  FileWithInfo,
  JobApiService,
} from '@aiwyn/api';
import {
  DEFAULT_SNACKBAR_CONFIG,
  Logger,
  SnackbarComponent,
  SnackbarType,
} from '@aiwyn/shared';
import { CxpDeliverableDetail } from '../models';
import { CxpDeliverableApiService } from './cxp-deliverable-api.service';
import { DeliverableDetailMappers } from './utils/deliverable-detail-mappers.utils';

const DELIVERABLE_NOT_FOUND_TAG = 'CXP_DELIVERABLE_NOT_FOUND';

class DeliverableNotFoundError extends Error {
  readonly tag = DELIVERABLE_NOT_FOUND_TAG;

  constructor(deliverableId: number) {
    super(`Deliverable ${deliverableId} not found`);
    this.name = 'DeliverableNotFoundError';
  }
}

/**
 * Orchestrates a self-sufficient fetch of every piece of data the deliverable
 * detail view needs: deliverable + job (for service line + client id) +
 * client + files. The page works on direct URL load because nothing depends
 * on the tasks-list state having been loaded first.
 */
@Injectable({ providedIn: 'root' })
export class CxpDeliverableDetailService {
  private readonly deliverableApi = inject(CxpDeliverableApiService);
  private readonly jobApi = inject(JobApiService);
  private readonly clientApi = inject(ClientApiService);
  private readonly fileApi = inject(FileApiService);
  private readonly snackbar = inject(MatSnackBar);
  private readonly logger = inject(Logger);

  fetch(deliverableId: number): Observable<CxpDeliverableDetail> {
    return this.deliverableApi.getDeliverableById(deliverableId).pipe(
      switchMap((apiResponse) => {
        if (!apiResponse) {
          return throwError(() => new DeliverableNotFoundError(deliverableId));
        }

        const fileIds = apiResponse.fileIds.map((entry) => entry.fileId);

        // Explicit typing on the empty observables — without it strict mode
        // resolves the ternaries to `Observable<X> | Observable<never[]>` which
        // breaks `.pipe(map(...))` overload resolution downstream.
        const filesWithInfo$: Observable<FileWithInfo[]> =
          fileIds.length > 0 ? this.fileApi.getFilesWithInfo(fileIds) : of<FileWithInfo[]>([]);

        return forkJoin({
          serviceLines: apiResponse.jobId !== null
            ? this.jobApi.getBaseServiceLinesByJobIds([apiResponse.jobId])
            : of([]),
          jobs: apiResponse.jobId !== null
            ? this.jobApi.getJobsByIds([apiResponse.jobId])
            : of([]),
          filesWithInfo: filesWithInfo$,
        }).pipe(
          switchMap(({ serviceLines, jobs, filesWithInfo }) => {
            const serviceLineName: string | null = apiResponse.jobId !== null
              ? serviceLines.find((sl) => sl.jobId === apiResponse.jobId)?.name ?? null
              : null;

            // Prefer client derived from the job; fall back to the deliverable's direct client_id
            const clientId: number | null = jobs?.[0]?.clientId ?? apiResponse.clientId ?? null;

            const clientName$: Observable<string | null> =
              clientId !== null
                ? this.clientApi.getBaseClients([clientId]).pipe(
                    map((clients) => clients[0]?.name ?? null),
                  )
                : of<string | null>(null);

            return clientName$.pipe(
              map((clientName) =>
                DeliverableDetailMappers.toDetail({
                  apiResponse,
                  filesWithInfo,
                  serviceLineName,
                  clientName,
                }),
              ),
            );
          }),
        );
      }),
      catchError((error: unknown) => {
        this.logger.error('[CxpDeliverableDetailService] fetch failed:', error);

        const message =
          error instanceof DeliverableNotFoundError
            ? 'We couldn’t find that deliverable. It may have been removed or you may not have access.'
            : 'Something went wrong loading this deliverable. Please refresh and try again.';

        this.showErrorToast(message);
        return throwError(() => error);
      }),
    );
  }

  private showErrorToast(message: string): void {
    this.snackbar.openFromComponent(SnackbarComponent, {
      ...DEFAULT_SNACKBAR_CONFIG,
      data: { message, type: SnackbarType.ERROR },
    });
  }
}
